import posthog from 'posthog-js';
import { isNetworkError } from './network-state';

/**
 * Scripts that in-app browsers and extensions inject into our pages. Their
 * errors land in our error tracking with our URL on them, but nothing of ours
 * is in the trace. Observed 2026-08-28: Instagram's Android in-app browser
 * injects `iabjs://navigation_performance_logger_android`, which throws
 * "Error invoking postMessage: Java object is gone" every time a visitor from
 * the link-in-bio navigates between dunea.ai and app.dunea.ai.
 */
const INJECTED_SCRIPT_SCHEMES = [
  'iabjs://',
  'gap://',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
];
const INJECTED_SCRIPT_MESSAGES = ['Java object is gone'];

/** True when an $exception came from an injected (non-Dunea) script. */
export function isInjectedScriptError(props: Record<string, unknown>): boolean {
  const message = String(props.$exception_message ?? '');

  if (INJECTED_SCRIPT_MESSAGES.some((m) => message.includes(m))) {
    return true;
  }

  const list = props.$exception_list as { stacktrace?: { frames?: { filename?: string }[] } }[] | undefined;
  const frames = (list ?? []).flatMap((e) => e.stacktrace?.frames ?? []);

  return (
    frames.length > 0 && frames.every((f) => INJECTED_SCRIPT_SCHEMES.some((s) => (f.filename ?? '').startsWith(s)))
  );
}

/**
 * Exception messages that are noise, not Dunea bugs. Substring match.
 */
const IGNORED_EXCEPTION_MESSAGES = [
  // Covers "ResizeObserver loop limit exceeded" and the synthetic
  // "ResizeObserver loop completed with undelivered notifications".
  'ResizeObserver loop',
  'Non-Error promise rejection',
  'Load failed',
  'NetworkError',
  // axios's wording for the same thing (no response at all).
  'Network Error',
  /*
   * Outlook / Microsoft Defender SafeLinks scanner: it opens emailed links in a
   * headless browser and rejects "Object Not Found Matching Id:N, MethodName:update,
   * ParamCount:4" from its own instrumentation. Seen on the marketing site.
   */
  'Object Not Found Matching Id',
];

/** True when an $exception message is known noise that before_send drops. */
export function isIgnoredExceptionMessage(message: unknown): boolean {
  const text = typeof message === 'string' ? message : '';

  return IGNORED_EXCEPTION_MESSAGES.some((ignored) => text.includes(ignored));
}

let _initialized = false;

export async function initPostHog() {
  if (_initialized) {
    return;
  }

  try {
    const res = await fetch('/api/config');
    const config = await res.json();

    if (!config.posthogKey) {
      return;
    }

    const serverHost = window.location.hostname;

    posthog.init(config.posthogKey, {
      api_host: config.posthogHost || '/ingest',
      ui_host: 'https://us.posthog.com',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      enable_heatmaps: true,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
      },
      persistence: 'localStorage+cookie',
      __add_tracing_headers: [serverHost],
      before_send: (event) => {
        if (!event) {
          return null;
        }

        if (event.event === '$exception') {
          const props: Record<string, unknown> = event.properties || {};
          props.$exception_environment = import.meta.env.MODE;
          props.$exception_url = window.location.href;
          event.properties = props;

          if (isIgnoredExceptionMessage(props.$exception_message)) {
            return null;
          }

          if (isInjectedScriptError(props)) {
            return null;
          }
        }

        return event;
      },
      loaded: () => {
        _initialized = true;
      },
    });
  } catch {
    // Config fetch failed — PostHog stays disabled
  }
}

export function capture(event: string, properties?: Record<string, any>) {
  if (!_initialized) {
    return;
  }

  posthog.capture(event, properties);
}

export function identify(userId: string, traits?: Record<string, any>) {
  if (!_initialized) {
    return;
  }

  posthog.identify(userId, traits);
}

export function reset() {
  if (!_initialized) {
    return;
  }

  posthog.reset();
}

export function isFeatureEnabled(flag: string): boolean {
  if (!_initialized) {
    return false;
  }

  return posthog.isFeatureEnabled(flag) ?? false;
}

export function getFeatureFlag(flag: string): string | boolean | undefined {
  if (!_initialized) {
    return undefined;
  }

  return posthog.getFeatureFlag(flag);
}

export function captureException(error: Error, properties?: Record<string, any>) {
  if (!_initialized) {
    return;
  }

  posthog.captureException(error, properties);
}

/**
 * Report a failed request. A request that never got a response (frozen phone
 * tab, offline) is counted as a plain event, not raised as an issue — those
 * filled the issue feed with one iPhone's chat saves. Real server errors (a
 * status came back) are still exceptions.
 */
export function captureRequestError(err: unknown, properties?: Record<string, any>) {
  if (isNetworkError(err)) {
    capture('request_network_failed', {
      ...properties,
      online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
      visibility: typeof document === 'undefined' ? undefined : document.visibilityState,
    });

    return;
  }

  captureException(err instanceof Error ? err : new Error(String(err)), properties);
}

export function addExceptionStep(stepName: string, properties?: Record<string, any>) {
  if (!_initialized) {
    return;
  }

  posthog.addExceptionStep(stepName, properties);
}
